import { Component, HostBinding, QueryList, ViewChildren } from '@angular/core';
import { RowNode, SelectionChangedEvent } from 'ag-grid-community';
import { createOrgNameOrIdRoleHandles, isArray, isUndefined } from '../../../../models/utils';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { Endpoint, HelpIndex, MembershipType, SettingsType, UpdateStatusType, UsersAndGroupsMembershipActionType } from '../../../../models/enums';
import { CellTemplateType, GenericColDef, GridRowSelectionType } from '../../../../models/grid-context';
import { Group } from '../../../../models/group';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { User } from '../../../../models/user';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { CreateEditEntityGroupModalComponent } from '../../create-edit-entity-group-modal/create-edit-entity-group-modal.component';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { MembershipModalComponent } from '../../membership-modal/membership-modal.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { UsersAndGroupsMembershipModalComponent } from '../../users-and-groups-membership-modal/users-and-groups-membership-modal.component';

@Component({
    standalone: false,
    selector: 'res-groups',
    templateUrl: './groups.component.html',
    styleUrls: ['./groups.component.scss']
})

export class GroupsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;

    isDirty: boolean;
    validChanges = true;
    selectedGroup: Group;
    numberOfSelectedGroups: number;
    newEditedGroup: Group;
    numberOfGroups: number;
    addMemberGroupSelectedNode: Group;
    hasGroupsPermission: boolean;
    hasUsersPermission: boolean;

    gridName = 'Groups';
    automationId = 'group-lists';
    groupsAutomationId = 'group-member-lists';
    helpIndex: any;
    isBusy = false;
    isBusyMessage = 'Loading Organization Groups ...';
    currentHandle = '';
    updateStatusType = UpdateStatusType.GROUP_VIEW;
    isSystemSettingsContext: boolean;
    newGroupBtn = 'New Organization Group ...';
    groupTypes = 'Organization Groups';
    groupMemberTypes = 'Organization Group Members';
    allMembers: Array<User>;
    updateAPICall = false;

    groupListSelection = GridRowSelectionType.single;
    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Group Name',
            field: 'name',
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.nameIconLabel,
            lockVisible: true
        },
        {
            headerName: 'Role',
            field: 'groupRole',
            automationId: this.automationId
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        }
    );

    colDefsGroupMenbers = new Array<GenericColDef>(
        {
            headerName: 'Username',
            field: 'userName',
            automationId: this.automationId,
            lockVisible: true
        },
        {
            headerName: 'Role',
            field: 'userRole',
            automationId: this.automationId
        },
        {
            headerName: 'Email',
            field: 'email',
            automationId: this.automationId
        }
    );

    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.orgManager_listGroups,
        responseName: 'groups',
        data: {}
    });

    requestInfoUsers = new DrRequestInfo({
        endpoint: Endpoint.groupManager_listUsers,
        responseName: 'users'
    });

    rowDisableGroupMemeber = {
        'disabled-row': () => {
            return !this.hasGroupsPermission;
        },
    };

    constructor(private commonService: CommonService, private restService: RestService) {
        if (this.isSystemSettingsContext) {
            this.requestInfoUsers.data = { systemScope: true };
        }
    }

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Add to / Remove from Projects ...',
            iconClass: 'fa-book', iconColor: 'color-grey-08', iconSecondClass: 'fa-pencil', iconSecondColor: 'color-blue-01',
            automationId: 'organization-settings-groups-add-to-remove-from-projects',
            click: (item) => this.addToRemoveFromProjsOrOrgs(item, UsersAndGroupsMembershipActionType.addToRemoveFromProjects),
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true,
            visible: (item: GenericTreeNode) => !this.isSystemSettingsContext, secondIconCustomClass: true
        }),
        new ContextMenuOption({
            name: 'Add to / Remove from Organizations ...',
            iconClass: 'fa-bank', iconColor: 'color-grey-08', iconSecondClass: 'fa-pencil', iconSecondColor: 'color-blue-01',
            automationId: 'system-settings-groups-add-to-remove-from-organizations',
            click: (item) => this.addToRemoveFromProjsOrOrgs(item, UsersAndGroupsMembershipActionType.addToRemoveFromOrganizations),
            permissions: [{ permissionType: ObjectActionListType.USERS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_USER, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true,
            visible: (item: GenericTreeNode) => this.isSystemSettingsContext, secondIconCustomClass: true
        }),
        new ContextMenuOption({ divider: true }),
        new ContextMenuOption({
            name: 'Edit ...', iconClass: 'fa-pencil', iconColor: 'color-blue-01', automationId: this.automationId,
            click: (item) => this.editGroup(item), permissions: [{ permissionType: ObjectActionListType.GROUPS, permissionActions: [ActionType.CREATE] },
            { permissionType: ObjectActionListType.SYSTEM_GROUP, permissionActions: [ActionType.CREATE] }],
            atLeastOne: true
        }),
        new ContextMenuOption({
            name: 'Delete', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: this.automationId,
            click: (item) => this.deleteGroup(item), permissions: [{ permissionType: ObjectActionListType.GROUPS, permissionActions: [ActionType.DELETE] },
            { permissionType: ObjectActionListType.SYSTEM_GROUP, permissionActions: [ActionType.DELETE] }],
            atLeastOne: true, enabled: (item) => this.isSystemSettingsContext || !item?.importedFromSystemGroup,
            visible: (item) => this.hasDeletePermission()
        })
    ];

    responseArrayCallbackUsers = (item: any) => new User(item);

    editGroup(item: Group): void {
        const group = new Group(item);
        this.commonService.openModal(CreateEditEntityGroupModalComponent, { modalData: { group, isSystemUser: this.isSystemSettingsContext } }).then((response) => {
            if (response && response.modalData && response.modalData) {
                this.newEditedGroup = response.modalData;
                this.genericGrids.first.loadData();
            }
        });
    }

    deleteGroup(item: Group): void {
        const group = new Group(item);
        Object.assign(group, { isSystemContext: this.isSystemSettingsContext });
        Object.assign(group, { title: this.isSystemSettingsContext ? 'Delete System Group' : 'Delete Organization Group' });
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: group }).then(() => {
            this.newEditedGroup = null;
        });
    }

    addNewGroup() {
        this.commonService.openModal(CreateEditEntityGroupModalComponent, { modalData: { isSystemUser: this.isSystemSettingsContext } })
            .then((response) => {
                if (response !== null && response.modalResult === true) {
                    this.newEditedGroup = new Group(response.modalData);
                    this.genericGrids.first.loadData();
                }
            });
    }

    addToRemoveFromProjsOrOrgs(item, action: UsersAndGroupsMembershipActionType): void {
        this.commonService.openModal(UsersAndGroupsMembershipModalComponent, {
            class: action === UsersAndGroupsMembershipActionType.addToRemoveFromProjects
                ? 'vertical-percent-80 horizontal-px-1200'
                : 'vertical-percent-80 horizontal-px-1000',
            modalData: {
                isMembershipModalOpenByUser: false,
                isMembershipModalOpenByAction: action,
                isSystemContext: this.isSystemSettingsContext,
                group: item
            }
        });
    }

    responseCallback = (response: any) => {
        if (response && isArray(response.groups)) {
            response.groups = (response.groups as Array<any>).map(g => new Group(g));
            this.selectedGroup = response.groups.length > 0 ? response.groups[0] : null;
            this.numberOfGroups = response.groups.length;
        }
    }

    loadDataFinishedCallback = () => {
        if (this.genericGrids.first.gridApi) {
            if (this.newEditedGroup) {
                this.genericGrids.first.gridApi.forEachNode((node) => {
                    if (this.newEditedGroup.handle === node.data.handle) {
                        node.setSelected(true);
                        this.genericGrids.first.gridApi.ensureNodeVisible(node);
                    }
                });
            } else if (this.selectedGroup && this.selectedGroup.handle) {
                this.genericGrids.first.gridApi.forEachNode((node) => {
                    if (this.selectedGroup.handle === node.data.handle) {
                        node.setSelected(true);
                    }
                });
            }
        }
    }

    onSelectionChanged = (event?: SelectionChangedEvent) => {
        if (event && event.api && this.genericGrids.first.gridApi) {
            this.selectedGroup = event.api.getSelectedRows()[0];
            if (this.selectedGroup) {
                this.genericGrids.last.requestInfo.data = {
                    groupHandle: this.selectedGroup.handle,
                    systemScope: this.isSystemSettingsContext
                };
                this.genericGrids.last.loadData();
            } else {
                this.genericGrids.last.setData([]);
            }
        }
    }

    cellDoubleClickedCallback = (rowNode: RowNode) => {
        if (rowNode && rowNode.data) {
            const selGroup = new Group(rowNode.data);
            if (selGroup) {
                this.editGroup(selGroup);
            }
        }
    }

    addGroupMembers(): void {
        const users = this.genericGrids.last.getData();
        this.isSystemSettingsContext = isUndefined(this.isSystemSettingsContext) ? this.groupTypes === 'System Groups' : this.isSystemSettingsContext;
        this.commonService.openModal(MembershipModalComponent, {
            class: 'vertical-percent-80 horizontal-px-1000',
            modalData: { leftList: [], rightList: users.sort(this.compare), membershipType: MembershipType.group, hideSystemUsers: this.isSystemSettingsContext ? false : true, isSystemSettingsContext: this.isSystemSettingsContext, isSystemGroupsOrSystemOrgs: this.isSystemSettingsContext, selectedGroup: this.selectedGroup }
        }).then((result) => {
            if (result.modalResult) {
                const userHandles = [];
                const members = [];
                result.modalData.forEach(element => {
                    members.push(element.data);
                    userHandles.push(element.data.name);
                });
                this.genericGrids.last.setData(members);
                this.newEditedGroup = this.selectedGroup;
                this.genericGrids.first.loadData();
            }
        });
    }

    private compare(a: any, b: any) {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        if (aName < bName) {
            return -1;
        } else if (aName > bName) {
            return 1;
        }
        return 0;
    }

    hasDeletePermission(): boolean {
        let hasDeletePermission = Permissions.checkUserPermission(ObjectActionListType.GROUPS, ActionType.DELETE);
        if (this.isSystemSettingsContext) {
            hasDeletePermission = Permissions.checkUserPermission(ObjectActionListType.SYSTEM_GROUP, ActionType.DELETE);
        }
        return hasDeletePermission;
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.isSystemSettingsContext = settingsType === SettingsType.system;
        if (this.isSystemSettingsContext) {
            this.requestInfo = new DrRequestInfo({
                endpoint: Endpoint.adminOrgManager_listUsersAndGroups,
                responseName: 'groups',
                data: { organizationName: 'super_system_customer', onlyGroups: true, onlyUsers: false, systemScope: this.isSystemSettingsContext }
            });
            this.newGroupBtn = 'New System Group ...';
            this.groupTypes = 'System Groups';
            this.groupMemberTypes = 'System Group Members';
            this.isBusyMessage = 'Loading System Groups ...';
        }
        this.helpIndex = this.isSystemSettingsContext ? HelpIndex.system_groups_manage_dlg : HelpIndex.org_groups_add_manage_dlg;
        this.hasGroupsPermission = Permissions.checkUserPermission(ObjectActionListType.GROUPS, ActionType.CREATE);
        this.hasUsersPermission = Permissions.checkUserPermission(ObjectActionListType.USERS, ActionType.VIEW);
        if (this.isSystemSettingsContext) {
            this.hasGroupsPermission = Permissions.checkUserPermission(ObjectActionListType.SYSTEM_GROUP, ActionType.CREATE);
            this.hasUsersPermission = Permissions.checkUserPermission(ObjectActionListType.SYSTEM_USER, ActionType.VIEW);
        }
        if (!this.hasGroupsPermission) {
            if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
            }
            this.contextMenuOptions = null;
        }
        if (this.genericGrids && this.genericGrids.first) {
            this.genericGrids.first.loadData();
        }
        this.allMembers = [];
    }

    saveChanges(): Promise<boolean> { return Promise.resolve(true); }
}
